import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  ClearingHouse,
  EntityIdAndType,
  EntityType,
  EntityTypeEnum,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  WorkflowActions
} from 'api'
import { createWorkflowReferenceDataAvailableEventsSelector, createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'

import './clearingHouseDialog.css'
import { ClearingHouseDialogComponent } from './ClearingHouseDialogComponent'
import { Locale, Translation } from 'locale'

export type ClearingHouseDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  entityId: string
  onClose?: () => unknown
}

interface StateProps {
  clearingHouseEntity: ReferenceDataEntityType<ClearingHouse> | null
  availableEvents: ReferenceDataManagementEvent[]
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: ClearingHouse; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: ClearingHouseDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: ClearingHouseDialogProps) => ownProps.entityId

const mapStateToProps = createStructuredSelector({
  clearingHouseEntity: createWorkflowReferenceDataEntitySelector(EntityType.CLEARING_HOUSE, workflowIdSelector, entityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

export const ClearingHouseDialogContainer: React.FC<ClearingHouseDialogProps> = props => {
  const { onUpdate, onClose, clearingHouseEntity, workflowId, availableEvents, onSubmitWorkflow, onCancelWorkflow } = props

  const clearingHouse = clearingHouseEntity?.data

  const readOnly = useMemo(() => !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE), [availableEvents])
  const hasValidationIssues = useMemo(() => !!clearingHouseEntity?.validationIssues && clearingHouseEntity.validationIssues.length > 0, [
    clearingHouseEntity
  ])

  const onUpdateClearingHouse = useCallback(
    (partialClearingHouse: Partial<ClearingHouse>) => {
      if (!clearingHouse) return

      onUpdate({
        entityId: clearingHouseEntity.id,
        entityType: EntityTypeEnum.CLEARING_HOUSE,
        updatedEntity: { ...clearingHouse, ...partialClearingHouse },
        workflowId
      })
    },
    [clearingHouse, clearingHouseEntity?.id, onUpdate, workflowId]
  )

  const handleSave = useCallback(() => {
    onSubmitWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }, [workflowId, onSubmitWorkflow, onClose])

  const handleClose = useCallback(() => {
    onCancelWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }, [onCancelWorkflow, onClose, workflowId])

  if (!clearingHouseEntity || !clearingHouse) {
    return null
  }

  return (
    <ClearingHouseDialogComponent
      title={Locale.translate(Translation.TC_EDIT_CLEARING_HOUSE_DETAILS)}
      clearingHouse={clearingHouse}
      readOnly={readOnly}
      hasValidationIssues={hasValidationIssues}
      onUpdate={onUpdateClearingHouse}
      onClose={handleClose}
      onSave={handleSave}
    />
  )
}

export const ClearingHouseEditDialog = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(ClearingHouseDialogContainer)
